import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ListChecks, RefreshCw, Search, ChevronLeft, ChevronRight, ChevronDown, ChevronRight as ChevronRightSmall, MapPin, ScanLine, Undo2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import * as greigeStorageApi from '@/services/greigeStorageApi';

const fmtDate = (iso) => {
  if (!iso) return '-';
  try { return new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' }); }
  catch { return iso; }
};

const ageBadge = (days) => {
  if (days == null || days < 0) return <Badge className="bg-gray-100 text-gray-500 border-0">-</Badge>;
  if (days <= 2) return <Badge className="bg-green-100 text-green-700 border-0">{days}d</Badge>;
  if (days <= 5) return <Badge className="bg-yellow-100 text-yellow-700 border-0">{days}d</Badge>;
  if (days <= 10) return <Badge className="bg-orange-100 text-orange-700 border-0">{days}d</Badge>;
  return <Badge className="bg-red-100 text-red-700 border-0">{days}d</Badge>;
};

const StatCard = ({ label, value, cls, testid }) => (
  <div className={`rounded-lg border p-4 ${cls}`} data-testid={testid}>
    <p className="text-xs opacity-80">{label}</p>
    <p className="text-2xl font-bold mt-1">{value ?? 0}</p>
  </div>
);

const PAGE_SIZE = 25;

const PickedNotTransferredReport = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ groups: [], total: 0, total_pages: 1, page: 1, summary: {}, can_manage: false });
  const [page, setPage] = useState(1);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [design, setDesign] = useState('');
  const [picker, setPicker] = useState('');
  const [minDays, setMinDays] = useState('');
  const [search, setSearch] = useState('');
  const [pendingOnly, setPendingOnly] = useState(true);
  const [expanded, setExpanded] = useState({});

  // Return-to-location dialog
  const [ret, setRet] = useState({ open: false, bundle: null, slipId: '', locationCode: '', reason: '', submitting: false });

  const fetchData = useCallback(async (pg = 1, override = {}) => {
    setLoading(true);
    try {
      const po = override.pendingOnly ?? pendingOnly;
      const params = { page: pg, page_size: PAGE_SIZE, pending_only: po };
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      if (design.trim()) params.design = design.trim();
      if (picker.trim()) params.picker = picker.trim();
      if (minDays !== '' && Number(minDays) > 0) params.min_days_waiting = Number(minDays);
      if (search.trim()) params.search = search.trim();
      const res = await greigeStorageApi.getPickedNotTransferredReport(params);
      setData(res);
      setPage(res.page || pg);
      // auto-expand all slips on the page for immediate visibility
      const exp = {};
      (res.groups || []).forEach((g) => { exp[g.pick_slip_id] = true; });
      setExpanded(exp);
    } catch (e) {
      toast.error('Failed to load report');
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo, design, picker, minDays, search, pendingOnly]);

  useEffect(() => { fetchData(1); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const applyFilters = () => fetchData(1);
  const clearFilters = async () => {
    setDateFrom(''); setDateTo(''); setDesign(''); setPicker(''); setMinDays(''); setSearch('');
    setLoading(true);
    try {
      const res = await greigeStorageApi.getPickedNotTransferredReport({ page: 1, page_size: PAGE_SIZE, pending_only: pendingOnly });
      setData(res);
      setPage(1);
      const exp = {};
      (res.groups || []).forEach((g) => { exp[g.pick_slip_id] = true; });
      setExpanded(exp);
    } catch (e) {
      toast.error('Failed to load report');
    } finally {
      setLoading(false);
    }
  };

  const togglePendingOnly = (val) => { setPendingOnly(val); fetchData(1, { pendingOnly: val }); };
  const toggleExpand = (id) => setExpanded((p) => ({ ...p, [id]: !p[id] }));

  const openReturn = (bundle, slipId) => setRet({ open: true, bundle, slipId, locationCode: '', reason: '', submitting: false });
  const submitReturn = async () => {
    if (!ret.locationCode.trim()) { toast.error('Scan a storage location first'); return; }
    setRet((p) => ({ ...p, submitting: true }));
    try {
      const res = await greigeStorageApi.returnPickedBundleToLocation(ret.bundle.bundle_code, ret.locationCode.trim(), ret.reason.trim());
      toast.success(res.message || `Bundle ${ret.bundle.bundle_code} returned to location`);
      setRet({ open: false, bundle: null, slipId: '', locationCode: '', reason: '', submitting: false });
      fetchData(page);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to return bundle');
      setRet((p) => ({ ...p, submitting: false }));
    }
  };

  const groups = data.groups || [];
  const summary = data.summary || {};
  const canManage = !!data.can_manage;

  return (
    <div className="min-h-screen bg-[#FFFBF5]">
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/sections/greige-storage')} className="text-[#F9FAFB] hover:bg-white/10" data-testid="pnt-back-btn">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-2">
              <ListChecks className="w-6 h-6" />
              <div>
                <h1 className="text-xl font-bold font-display">Pick Slip Status</h1>
                <p className="text-sm text-[#E8DCC8]">Greige Storage — picked bundles & internal-transfer status</p>
              </div>
            </div>
          </div>
          <Button variant="outline" onClick={() => fetchData(page)} className="bg-white/10 text-white border-white/20 hover:bg-white/20" data-testid="pnt-refresh-btn">
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 md:px-6 py-8">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <StatCard label="Pick slips" value={summary.total_slips} cls="bg-indigo-50 border-indigo-200 text-indigo-800" testid="pnt-stat-slips" />
          <StatCard label="Bundles shown" value={summary.total_bundles} cls="bg-amber-50 border-amber-200 text-amber-800" testid="pnt-stat-bundles" />
          <StatCard label="Pending pieces" value={summary.pending_pieces} cls="bg-rose-50 border-rose-200 text-rose-800" testid="pnt-stat-pending" />
          <StatCard label="Transferred pieces" value={summary.transferred_pieces} cls="bg-green-50 border-green-200 text-green-800" testid="pnt-stat-transferred" />
        </div>

        <div className="bg-white rounded-lg border border-[#E5DFD0] p-4 mb-4">
          <div className="flex flex-col lg:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input value={search} onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && applyFilters()}
                placeholder="Search slip / design / picker / bundle / sort / shade / colour..." className="pl-10" data-testid="pnt-search" />
            </div>
            <div className="flex flex-wrap gap-2 items-center">
              <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="w-40" title="Picked from" data-testid="pnt-date-from" />
              <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="w-40" title="Picked to" data-testid="pnt-date-to" />
            </div>
          </div>
          <div className="flex flex-wrap gap-2 mt-3 items-center">
            <Input value={design} onChange={(e) => setDesign(e.target.value)} placeholder="Design..." className="w-48" data-testid="pnt-design" />
            <Input value={picker} onChange={(e) => setPicker(e.target.value)} placeholder="Picker..." className="w-48" data-testid="pnt-picker" />
            <Input type="number" min="0" value={minDays} onChange={(e) => setMinDays(e.target.value)} placeholder="Min days waiting" className="w-40" data-testid="pnt-min-days" />
            <Button onClick={applyFilters} className="bg-rose-600 hover:bg-rose-700 text-white" data-testid="pnt-apply-btn">Apply</Button>
            <Button variant="outline" onClick={clearFilters} data-testid="pnt-clear-btn">Clear</Button>
            <div className="flex items-center gap-2 ml-auto bg-[#FFFBF5] border border-[#E5DFD0] rounded-md px-3 py-2">
              <Switch id="pending-only" checked={pendingOnly} onCheckedChange={togglePendingOnly} data-testid="pnt-pending-toggle" />
              <Label htmlFor="pending-only" className="text-sm cursor-pointer">Pending only (picked, not transferred)</Label>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="bg-white rounded-lg border border-[#E5DFD0] py-16 text-center text-[#5A5F6E]">Loading…</div>
        ) : groups.length === 0 ? (
          <div className="bg-white rounded-lg border border-[#E5DFD0] py-16 text-center text-[#5A5F6E]" data-testid="pnt-empty">
            {pendingOnly ? 'No picked bundles awaiting transfer 🎉' : 'No picked bundles found'}
          </div>
        ) : (
          <div className="space-y-3">
            {groups.map((g) => {
              const open = !!expanded[g.pick_slip_id];
              return (
                <div key={g.pick_slip_id} className="bg-white rounded-lg border border-[#E5DFD0] overflow-hidden" data-testid={`pnt-slip-${g.pick_slip_id}`}>
                  <button
                    className="w-full flex items-center justify-between gap-3 px-4 py-3 hover:bg-[#FFFBF5] text-left"
                    onClick={() => toggleExpand(g.pick_slip_id)}
                    data-testid={`pnt-slip-header-${g.pick_slip_id}`}
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      {open ? <ChevronDown className="w-4 h-4 shrink-0 text-[#5A5F6E]" /> : <ChevronRightSmall className="w-4 h-4 shrink-0 text-[#5A5F6E]" />}
                      {ageBadge(g.days_waiting)}
                      <div className="min-w-0">
                        <p className="font-semibold text-[#1A1F2E] truncate">{g.design_name || '-'}</p>
                        <p className="text-xs text-[#5A5F6E] font-mono truncate">{g.pick_slip_id} · Picker: {g.picker_name || '-'} · {fmtDate(g.completed_at)}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4 shrink-0 text-sm">
                      <div className="text-center">
                        <p className="text-[10px] uppercase text-[#5A5F6E]">Target/Picked</p>
                        <p className="font-semibold text-[#1A1F2E]">{g.target_qty} / {g.picked_qty}</p>
                      </div>
                      <div className="text-center">
                        <p className="text-[10px] uppercase text-[#5A5F6E]">IT Qty</p>
                        <p className="font-bold text-green-700" data-testid={`pnt-slip-itqty-${g.pick_slip_id}`}>{g.internal_transfer_qty}</p>
                      </div>
                      <div className="text-center">
                        <p className="text-[10px] uppercase text-[#5A5F6E]">Pending</p>
                        <p className="font-bold text-rose-700" data-testid={`pnt-slip-pending-${g.pick_slip_id}`}>{g.pending_count}</p>
                      </div>
                    </div>
                  </button>
                  {open && (
                    <div className="overflow-x-auto border-t border-[#E5DFD0]">
                      <Table data-testid={`pnt-table-${g.pick_slip_id}`}>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Bundle</TableHead>
                            <TableHead>Sort No</TableHead>
                            <TableHead>Shade</TableHead>
                            <TableHead>Colour</TableHead>
                            <TableHead className="text-center">Qty</TableHead>
                            <TableHead className="text-center">Internal Transfer Qty</TableHead>
                            <TableHead>Status</TableHead>
                            {canManage && <TableHead className="text-right">Action</TableHead>}
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {(g.bundles || []).map((b) => (
                            <TableRow key={b.bundle_code} data-testid={`pnt-row-${b.bundle_code}`}>
                              <TableCell className="font-mono text-sm">{b.bundle_code}</TableCell>
                              <TableCell>{b.sort_no || '-'}</TableCell>
                              <TableCell>{b.shade || '-'}</TableCell>
                              <TableCell>{b.color_name || '-'}</TableCell>
                              <TableCell className="text-center font-semibold">{b.qty} {b.qty_unit || ''}</TableCell>
                              <TableCell className="text-center font-semibold" data-testid={`pnt-itqty-${b.bundle_code}`}>{b.internal_transfer_qty}</TableCell>
                              <TableCell>
                                {b.transferred
                                  ? <Badge className="bg-green-100 text-green-700 border-0">Transferred</Badge>
                                  : <Badge className="bg-rose-100 text-rose-700 border-0">Pending</Badge>}
                              </TableCell>
                              {canManage && (
                                <TableCell className="text-right">
                                  {b.can_return && (
                                    <Button size="sm" variant="outline" className="h-8 border-[#2D5F4F] text-[#2D5F4F] hover:bg-[#2D5F4F]/10"
                                      onClick={() => openReturn(b, g.pick_slip_id)} data-testid={`pnt-return-btn-${b.bundle_code}`}>
                                      <Undo2 className="w-3.5 h-3.5 mr-1" /> Return to Location
                                    </Button>
                                  )}
                                </TableCell>
                              )}
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {!loading && groups.length > 0 && (
          <div className="flex items-center justify-between mt-4">
            <p className="text-sm text-[#5A5F6E]" data-testid="pnt-page-info">
              Page {data.page} of {data.total_pages} · {data.total} pick slip{data.total === 1 ? '' : 's'}
            </p>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" disabled={data.page <= 1} onClick={() => fetchData(data.page - 1)} data-testid="pnt-prev-btn">
                <ChevronLeft className="w-4 h-4" /> Prev
              </Button>
              <Button variant="outline" size="sm" disabled={data.page >= data.total_pages} onClick={() => fetchData(data.page + 1)} data-testid="pnt-next-btn">
                Next <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </main>

      {/* Return-to-Location dialog */}
      <Dialog open={ret.open} onOpenChange={(o) => !o && setRet((p) => ({ ...p, open: false }))}>
        <DialogContent data-testid="pnt-return-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Undo2 className="w-5 h-5" /> Return bundle to location</DialogTitle>
            <DialogDescription>
              Scan the storage bin where you are physically placing bundle <span className="font-mono font-semibold">{ret.bundle?.bundle_code}</span>. It will be re-stored and removed from this report.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label className="text-sm flex items-center gap-1.5 mb-1"><MapPin className="w-4 h-4" /> Storage location</Label>
              <div className="relative">
                <ScanLine className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  autoFocus
                  value={ret.locationCode}
                  onChange={(e) => setRet((p) => ({ ...p, locationCode: e.target.value }))}
                  onKeyDown={(e) => e.key === 'Enter' && !ret.submitting && submitReturn()}
                  placeholder="Scan bin / footprint label + Enter..."
                  className="pl-10 h-12"
                  data-testid="pnt-return-location-input"
                />
              </div>
            </div>
            <div>
              <Label className="text-sm mb-1 block">Reason (optional)</Label>
              <Input value={ret.reason} onChange={(e) => setRet((p) => ({ ...p, reason: e.target.value }))}
                placeholder="e.g. leftover / not required for shipment" data-testid="pnt-return-reason" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRet((p) => ({ ...p, open: false }))} data-testid="pnt-return-cancel">Cancel</Button>
            <Button className="bg-[#2D5F4F] hover:bg-[#1A1F2E]" disabled={ret.submitting || !ret.locationCode.trim()} onClick={submitReturn} data-testid="pnt-return-confirm">
              {ret.submitting ? 'Placing…' : 'Confirm & Place'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PickedNotTransferredReport;
